// A1 — the Fix-and-Prove ledger (PRD 16 §4), the panel D1 chose to anchor the
// analytics screen. *"You rewrote `idempotency key` on Mar 4. Readers looked
// it up 62% less often over the next 14 days."*
//
// This is the artifact a docs manager forwards to their VP, which makes it the
// one panel where a plausible-but-false number does real damage. Three things
// are therefore deliberate and should not be "tidied":
//
//  1. **Every delta says "per visit".** The backend divides look-ups by
//     sessions precisely so a traffic drop cannot read as an improvement; the
//     qualifier travels with the number so the reader knows which ratio it is.
//     The measure is `hover_count / unique_sessions` — directly observed, not
//     a weighted composite (that was `confusion_score`, and it is gone).
//  2. **`not_measured` rows are rendered, not hidden.** They carry the
//     backend's own reason. Hiding them would turn "three of eight edits could
//     be judged" into an apparent eight-for-eight — the same lie by omission.
//  3. **There is no export affordance.** PRD 16 §8 rejects raw event export,
//     and an "export" button on the one panel that reads like a report is the
//     shortest path back to it. Aggregate CSV is a Scale line item, not a
//     button someone adds here.

import { Badge, Card, EmptyState, List, ListRow } from "../../ui";
import type { LedgerEntry, LedgerWindow } from "./api";
import {
  formatCount,
  formatDelta,
  formatMagnitude,
  formatScore,
  shownOf,
} from "./format";
import {
  AFTER_LABEL,
  BEFORE_LABEL,
  DELTA_WHY,
  LEDGER_EMPTY_BODY,
  LEDGER_EMPTY_HEADLINE,
  LEDGER_SUBTITLE,
  LEDGER_TITLE,
  LOOKUPS_FELL,
  LOOKUPS_FLAT,
  LOOKUPS_ROSE,
  NOT_MEASURED,
  NO_VERDICT_WHY,
  PER_VISIT_LABEL,
  SESSIONS_LABEL,
  WINDOW_INCOMPLETE,
} from "./metricLabels";
import "./analytics.css";

const MAX_ROWS = 6;

/** `edited_at` is naive UTC (DuckDB's shape). Rendered as a plain calendar day
 *  — the hour is never the point, and a timezone suffix would only invite a
 *  reader to compare it against their own clock. */
function editedOn(iso: string): string {
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return iso;
  return parsed.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function tone(verdict: LedgerEntry["verdict"]) {
  if (verdict === "improved") return "nominal" as const;
  if (verdict === "worsened") return "flagged" as const;
  return "neutral" as const;
}

/** The headline claim, in words. Carries the direction, so the number beside it
 *  is a magnitude — never a signed value, which would read "fell −62%". */
function headline(entry: LedgerEntry): string {
  if (entry.verdict === "unchanged") return LOOKUPS_FLAT;
  const word = entry.verdict === "improved" ? LOOKUPS_FELL : LOOKUPS_ROSE;
  return `${word} ${formatMagnitude(entry.lookups_delta_pct)} ${PER_VISIT_LABEL}`;
}

/** "before: 200 sessions · 3.0 per visit". A rate off zero visits is undefined
 *  rather than zero, so it renders as "not measured". */
function windowLine(label: string, window: LedgerWindow): string {
  const rate =
    window.lookups_per_session === null
      ? NOT_MEASURED
      : `${formatScore(window.lookups_per_session)} ${PER_VISIT_LABEL}`;
  return `${label}: ${formatCount(window.sessions)} ${SESSIONS_LABEL} · ${rate}`;
}

export type FixAndProveProps = {
  entries: LedgerEntry[];
  /** Rewrites found before measurability was considered. Zero means nobody has
   *  edited anything — a different sentence from "we could not measure them". */
  editsFound: number;
  windowDays: number;
};

export function FixAndProve({
  entries,
  editsFound,
  windowDays,
}: FixAndProveProps) {
  const shown = entries.slice(0, MAX_ROWS);

  return (
    <Card
      header={LEDGER_TITLE}
      footer={
        <span className="type-bodyS">
          {editsFound === 0
            ? LEDGER_SUBTITLE
            : `${shownOf(shown.length, entries.length, "rewrite")} · ${windowDays} days either side`}
        </span>
      }
    >
      {editsFound === 0 ? (
        <EmptyState headline={LEDGER_EMPTY_HEADLINE} body={LEDGER_EMPTY_BODY} />
      ) : (
        <List label={LEDGER_TITLE}>
          {shown.map((entry) => {
            const measured = entry.verdict !== "not_measured";
            return (
              <ListRow
                key={`${entry.doc_id}:${entry.slug}:${entry.edited_at}`}
                title={
                  <span className="maisight-analytics-ledger-term">
                    {entry.term}
                  </span>
                }
                meta={
                  <span className="maisight-analytics-ledger-meta">
                    <span>
                      {`${entry.doc_id} · rewritten ${editedOn(entry.edited_at)} by ${entry.editor}`}
                    </span>
                    <span
                      className="maisight-analytics-metric"
                      data-measured={measured ? "yes" : "no"}
                    >
                      {measured
                        ? `${headline(entry)} — ${windowLine(BEFORE_LABEL, entry.before)} → ${windowLine(AFTER_LABEL, entry.after)}`
                        : `${NO_VERDICT_WHY} ${entry.not_measured_reason ?? ""}`}
                    </span>
                    {measured && !entry.after_window_complete ? (
                      <span className="maisight-analytics-ledger-open">
                        {WINDOW_INCOMPLETE}
                      </span>
                    ) : null}
                  </span>
                }
                trailing={
                  <Badge tone={tone(entry.verdict)}>
                    {measured ? (
                      <span title={DELTA_WHY}>
                        {formatDelta(entry.lookups_delta_pct)}
                      </span>
                    ) : (
                      NOT_MEASURED
                    )}
                  </Badge>
                }
              />
            );
          })}
        </List>
      )}
    </Card>
  );
}
